/**
 * @file caseRespond.js
 * @description Client side of the one-click case-expiry response flow — the
 *   "Reconfirm this listing" / "Already filled? Mark this case filled" links
 *   in §1 expiry-notice emails (send-case-activity-alerts). Mirrors
 *   interviewRespond.js's shape and reasoning exactly; see that file for the
 *   parts only briefly restated here.
 *
 *   What lives here is transport only. The page this feeds
 *   (CaseRespondPage.jsx) is presentation; it never decides whether an action
 *   is allowed. Params are passed through untouched to the `case-respond`
 *   edge function, which re-verifies its HMAC (a SEPARATE, case-only
 *   signature — see actionToken.ts — never the interview one) on every call.
 *
 *   Deliberately does NOT use the shared supabase client: this page must work
 *   logged out, must not attach the reader's session to the call (mail gets
 *   forwarded, and agency inboxes like `info@` are routinely shared), and the
 *   function runs with `verify_jwt = false` so no key is needed.
 *
 * @module lib/caseRespond
 */
import { supabaseUrl } from "./supabase";

/** Public route the email links point at. Intentionally NOT in seoRoutes.json. */
export const CASE_RESPOND_PATH = "/case-respond";

const FN_URL = `${supabaseUrl}/functions/v1/case-respond`;

/** True when the given pathname is the case-respond route (trailing slash tolerated). */
export function isCaseRespondPath(pathname) {
  return String(pathname || "").replace(/\/+$/, "") === CASE_RESPOND_PATH;
}

/**
 * Pull the signed case-action params out of a query string.
 * Case links use `c` (case id) where interview links use `i` (invite id) —
 * see actionToken.ts's CaseActionClaims — so the two are never confused even
 * if a query string somehow carried both.
 * @param {string} search e.g. window.location.search
 * @returns {URLSearchParams|null} null when the link is obviously incomplete
 */
export function readCaseActionParams(search) {
  let q;
  try { q = new URLSearchParams(search || ""); } catch { return null; }
  const c = q.get("c"), a = q.get("a"), e = q.get("e"), sig = q.get("sig");
  if (!c || !a || !e || !sig) return null;
  const out = new URLSearchParams();
  out.set("c", c);
  out.set("a", a);
  out.set("s", q.get("s") || "");
  out.set("e", e);
  out.set("sig", sig);
  return out;
}

/**
 * Call the edge function with the signed params (plus, for the `filled`
 * action's second step, the UNSIGNED `reason` the agency picked on this
 * page — see case-respond/index.ts's file header for why that value rides
 * unsigned rather than living in the link itself).
 *
 * GET is a preview and never writes. POST performs the action. Params travel
 * in the query string with no request body or Content-Type, keeping the POST
 * a CORS "simple request" (no preflight) while the function still enforces
 * its own Origin allow-list.
 *
 * @param {"GET"|"POST"} method
 * @param {URLSearchParams} params signed params from readCaseActionParams
 * @param {{reason?: string}} [extra] extra UNSIGNED params (e.g. `reason` for `a=filled`)
 * @returns {Promise<{ok: boolean, state: string, [k: string]: any}>}
 *   Always resolves. Transport failures surface as `{ state: "network" }` so
 *   the page can tell "we couldn't reach the server" apart from "the server
 *   said no".
 */
export async function callCaseRespond(method, params, extra = {}) {
  const qs = new URLSearchParams(params);
  if (extra.reason) qs.set("reason", extra.reason);

  let res;
  try {
    res = await fetch(`${FN_URL}?${qs.toString()}`, {
      method,
      // No cookies, no Authorization header: acting on this link must never
      // ride on, or create, a session.
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
  } catch {
    return { ok: false, state: "network", reason: "fetch_failed" };
  }
  // The gateway serves this as text/plain regardless of what the function
  // sets, so parse the text rather than trusting res.json()'s content-type path.
  let body = null;
  try { body = JSON.parse(await res.text()); } catch { /* fall through */ }
  if (!body || typeof body !== "object") {
    return { ok: false, state: "network", reason: "bad_response", httpStatus: res.status };
  }
  return { ...body, httpStatus: res.status };
}
